import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { trackConversion } from "../lib/gtag";
import { EuStars } from "../components/EuStars";

/**
 * Post-payment conversion page: /checkout/success/:sessionId
 * Renders ONLY for the session owner, only after webhook-confirmed payment,
 * and only ONCE per order (reloads bounce straight to the dashboard),
 * safe to use as a Google Ads conversion trigger via URL match
 * "begins with https://eurolawhub.com/checkout/success/".
 */
export default function CheckoutSuccess() {
    const { sessionId } = useParams();
    const navigate = useNavigate();
    const { user, loading, refresh } = useAuth();
    const [order, setOrder] = useState(null);
    const askedRef = useRef(false);

    useEffect(() => {
        if (loading) return;
        if (!user) {
            navigate(`/login?next=/checkout/success/${sessionId}`, { replace: true });
            return;
        }
        if (askedRef.current) return;
        askedRef.current = true;
        api.post(`/billing/checkout/${sessionId}/confirmation`)
            .then(async ({ data }) => {
                if (data.already_confirmed) {
                    navigate("/app", { replace: true });
                    return;
                }
                setOrder(data);
                trackConversion({
                    value: data.amount_eur,
                    transactionId: sessionId,
                });
                await refresh?.();
            })
            .catch((err) => {
                const st = err?.response?.status;
                if (st === 409) {
                    navigate(`/app/billing/checkout/${sessionId}`, { replace: true });
                } else {
                    navigate("/app/pricing", { replace: true });
                }
            });
    }, [loading, user, sessionId, navigate, refresh]);

    if (!order) {
        return (
            <div className="min-h-screen bg-[#F5F0E8] grid place-items-center">
                <div className="flex items-center gap-3 text-[#0D1B3E]/60">
                    <Loader2 size={18} className="animate-spin" /> Confirming your order…
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F5F0E8] flex items-center justify-center p-6"
            data-testid="checkout-success-page">
            <div className="w-full max-w-lg text-center">
                <div className="mx-auto w-16 h-16 rounded-full bg-[#0D1B3E] grid place-items-center">
                    <CheckCircle2 size={30} color="#C9A84C" />
                </div>
                <div className="label-eyebrow mt-6">Payment confirmed</div>
                <h1 className="font-display text-4xl mt-3 tracking-tight" data-testid="checkout-success-title">
                    Welcome to {order.plan_name}
                </h1>
                <p className="mt-4 text-[#0D1B3E]/75">
                    Your subscription is active, billed {order.cycle === "annual" ? "annually" : "monthly"}
                    {order.plan_key === "team" ? ` · ${order.seats} seats` : ""}. Your monthly
                    credits and full feature set are unlocked, and your receipt is available
                    on the billing portal.
                </p>
                <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
                    <Link to="/app" className="btn-primary" data-testid="checkout-success-dashboard">
                        Go to Dashboard
                    </Link>
                    <Link to="/app/billing/portal" className="btn-secondary" data-testid="checkout-success-billing">
                        View billing
                    </Link>
                </div>
                <div className="mt-12 flex items-center justify-center gap-3 text-[#0D1B3E]/50">
                    <EuStars size={22} />
                    <span className="text-xs uppercase tracking-[0.22em]">EuroLaw Hub</span>
                </div>
            </div>
        </div>
    );
}
